import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Phone, 
  Shield, 
  Trash2, 
  Loader2,
  CheckCircle,
  XCircle,
  Edit
} from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const UsersManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    phone: ''
  });

  const roles = [
    { value: 'user', label: 'Utilisateur', color: 'bg-gray-100 text-gray-700' },
    { value: 'secretary', label: 'Secrétaire', color: 'bg-blue-100 text-blue-700' },
    { value: 'supervisor', label: 'Superviseur', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'manager', label: 'Manager', color: 'bg-purple-100 text-purple-700' },
    { value: 'admin', label: 'Administrateur', color: 'bg-red-100 text-red-700' }
  ];

  const fetchUsers = async () => {
    try {
      // On va chercher les utilisateurs via l'API /api/auth/me pour l'instant
      // Note: Une API dédiée serait nécessaire pour lister tous les utilisateurs
      const response = await api.get('/auth/me');
      // Pour l'instant, on affiche juste l'utilisateur courant comme exemple
      // Une vraie implémentation nécessiterait un endpoint /api/users
      setUsers([{
        id: response.data.user.id,
        name: response.data.user.name,
        email: response.data.user.email,
        role: response.data.user.role,
        isActive: true
      }]);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast({ 
        title: 'Erreur', 
        description: 'Nom, email et mot de passe sont obligatoires', 
        variant: 'destructive' 
      });
      return;
    }

    // Validation stricte du mot de passe
    const passwordErrors = [];
    if (newUser.password.length < 8) {
      passwordErrors.push('minimum 8 caractères');
    }
    if (!/[A-Z]/.test(newUser.password)) {
      passwordErrors.push('une majuscule');
    }
    if (!/[a-z]/.test(newUser.password)) {
      passwordErrors.push('une minuscule');
    }
    if (!/[0-9]/.test(newUser.password)) {
      passwordErrors.push('un chiffre');
    }
    if (!/[!@#$%^&*(),.?":{}|<>\-_=+]/.test(newUser.password)) {
      passwordErrors.push('un caractère spécial (!@#$%...)');
    }
    
    if (passwordErrors.length > 0) {
      toast({ 
        title: '🔒 Mot de passe trop faible', 
        description: `Requis: ${passwordErrors.join(', ')}`, 
        variant: 'destructive' 
      });
      return;
    }

    // Validation email basique
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newUser.email)) {
      toast({ 
        title: 'Erreur', 
        description: 'Format email invalide', 
        variant: 'destructive' 
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/auth/register-admin', newUser);
      
      toast({ 
        title: '✅ Utilisateur créé', 
        description: `${response.data.message}` 
      });
      
      // Reset form
      setNewUser({ name: '', email: '', password: '', role: 'user', phone: '' });
      setShowAddForm(false);
      
      // Add to local state
      setUsers(prev => [...prev, {
        id: response.data.userId,
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        isActive: true
      }]);
      
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Impossible de créer l\'utilisateur', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleStyle = (role) => {
    return roles.find(r => r.value === role)?.color || 'bg-gray-100 text-gray-700';
  };

  const getRoleLabel = (role) => {
    return roles.find(r => r.value === role)?.label || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            Gestion des Utilisateurs
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez les comptes utilisateurs de votre entreprise
          </p>
        </div>
        <Button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Créer un nouvel utilisateur
            </CardTitle>
            <CardDescription>
              L'utilisateur recevra ses identifiants pour se connecter
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    value={newUser.name}
                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                    placeholder="Jean Dupont"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    placeholder="jean.dupont@entreprise.fr"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    placeholder="Minimum 8 caractères"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="role">Rôle</Label>
                  <select
                    id="role"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowAddForm(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Création...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Créer l'utilisateur
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Utilisateurs ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-gray-600 font-medium">Utilisateur</th>
                  <th className="text-left p-3 text-gray-600 font-medium">Email</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Rôle</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Statut</th>
                  <th className="text-center p-3 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.phone || 'Pas de téléphone'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        {user.email}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleStyle(user.role)}`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {user.isActive ? (
                        <span className="flex items-center justify-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          Actif
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-800">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {users.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucun utilisateur pour le moment</p>
              <Button 
                onClick={() => setShowAddForm(true)}
                variant="outline"
                className="mt-4"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ajouter le premier utilisateur
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-amber-600 mt-1" />
            <div>
              <h3 className="font-semibold text-amber-900">Rôles et permissions</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                <li><strong>Utilisateur:</strong> Accès lecture seule aux données</li>
                <li><strong>Secrétaire:</strong> Gestion de la facturation et des documents</li>
                <li><strong>Superviseur:</strong> Gestion des agents et du planning</li>
                <li><strong>Manager:</strong> Accès complet sauf gestion utilisateurs</li>
                <li><strong>Administrateur:</strong> Accès total, gestion utilisateurs</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersManagement;
